// 保持这个脚本在入口文件的顶部引入。
import React from 'react';
import App from 'next/app';
import dynamic from 'next/dynamic';
import { defineNextApp } from '@/common/next';
// CSS 加载顺序：后加载的优先级更高
import '@/common/font.scss'; // 字体定义
// SDK 全局样式（主题 token、antd、画布等，原 packages/roboneo-sdk/src/index.ts 侧效导入）
/** -- antd start -- */
import 'antd/lib/modal/style/css';
import 'antd/lib/popover/style/css';
import 'antd/lib/tooltip/style/css';
import 'antd/lib/select/style/css';
import 'antd/lib/input/style/css';
import 'antd/lib/slider/style/css';
import 'antd/lib/drawer/style/css';
import 'antd/lib/switch/style/css';
import 'antd/lib/table/style/css';
/** -- antd end -- */
import '$/style/index.scss';
import '$/style/theme_dark.scss';
import '$/style/theme_light.scss';
import '$/style/var.scss';
import '$/style/resetAntd.scss';
import '$/style/teamChat';
// import '@/common/common.scss'; // 基础重置样式
import '../styles/global.css'; // Tailwind 样式（放最后，优先级最高）
import '../styles/global.scss';

import '@meitu/account-login-popup/dist/style.css';
import '@/polyfills/core-js-shim';
import 'intersection-observer';

import { resetGlobalPreloads } from '$/components/NextImage/ImagePreloadContext';
import { initBrowserSentry } from '@/common/sentry';
import SvgIconServiceWorkerRegistrar from '@/components/SvgIconServiceWorkerRegistrar';
import ImageCacheServiceWorkerRegistrar from '@/components/ImageCacheServiceWorkerRegistrar';
import WebApmLifecycle from '@/components/WebApmLifecycle';
import OverseaPageErrorBoundary from '@/components/overseaHome/OverseaPageErrorBoundary';
import { getDomesticOfficialHomeRedirectUrl, isOfficialHomePath } from '@/utils/officialHome';
import { useOfficialStore } from '$/store/useOfficialStore';

// 在动态加载各业务子应用前启用浏览器性能采集，避免漏掉初始化阶段的网络请求。
initBrowserSentry();

// 动态加载
const MobileApp = dynamic(() => import('./--mobile/SubApp').then((mod) => mod.default.resolve()));
const RoboneoApp = dynamic(() => import('./--roboneo/SubApp').then((mod) => mod.default.resolve()));
const OverseaRoboneoApp = dynamic(() => import('./--oversea/SubApp').then((mod) => mod.default.resolve()));

function parseCookieString(cookieStr: string) {
  const cookies: Record<string, string> = {};
  if (!cookieStr) return cookies;

  const pairs = cookieStr.split('; ');
  for (const pair of pairs) {
    const [key, ...valParts] = pair.split('=');
    cookies[key] = decodeURIComponent(valParts.join('='));
  }
  return cookies;
}
export default defineNextApp({
  /** 两个官网首页共用官网运行时；国内跳转期间不挂载官网布局和初始化逻辑。 */
  component: (appProps) => {
    if (isOfficialHomePath(appProps.router.pathname) && appProps.pageProps.officialHomeRedirecting === true) {
      return null;
    }
    const currentProjectId = appProps.Component?.pageConfig?.projectId;

    let pageContent: React.ReactNode;

    if (currentProjectId === '/--mobile') {
      pageContent = <MobileApp appProps={appProps} />;
    } else if (currentProjectId === '/--roboneo' && !isOfficialHomePath(appProps.router.pathname)) {
      pageContent = <RoboneoApp appProps={appProps} />;
    } else if (currentProjectId === '/--oversea' || isOfficialHomePath(appProps.router.pathname)) {
      pageContent = (
        <OverseaPageErrorBoundary>
          <OverseaRoboneoApp appProps={appProps} />
        </OverseaPageErrorBoundary>
      );
    } else {
      pageContent = <appProps.Component />;
    }

    return (
      <>
        <WebApmLifecycle />
        <SvgIconServiceWorkerRegistrar />
        <ImageCacheServiceWorkerRegistrar />
        {pageContent}
      </>
    );
  },
  /** 国内客户端导航先分流；其他请求获取页面数据并复用服务端解析的国家码。 */
  async getInitialProps(appContext) {
    // 每个 SSR 请求开始时重置 preload 收集器
    resetGlobalPreloads();

    const { req, query } = appContext.ctx;
    const isOfficialHome = isOfficialHomePath(appContext.ctx.pathname || '');
    // 客户端切页不经过 Express，在页面数据请求之前拦截，并跳过整个官网运行时。
    if (!req && typeof window !== 'undefined' && isOfficialHome) {
      const countryCode = useOfficialStore.getState().currentCountryCode;
      const redirectUrl = getDomesticOfficialHomeRedirectUrl(
        countryCode,
        process.env.NEXT_PUBLIC_ENV === 'development',
        appContext.ctx.asPath || '',
      );
      if (redirectUrl) {
        window.location.replace(redirectUrl);
        return { pageProps: { countryCode, officialHomeRedirecting: true } };
      }
    }

    const appProps = await App.getInitialProps(appContext);
    const requestWithCountryCode = req as (typeof req & { countryCode?: string; }) | undefined;
    const injectedCountryCode = requestWithCountryCode?.countryCode?.trim();
    // 官网客户端导航无 req 时，保留页面数据层取得的地区和设备信息。
    let countryCode = injectedCountryCode || (isOfficialHome ? appProps.pageProps.countryCode : '') || 'CN';
    const cookieHeader = req?.headers.cookie;
    const cookies = parseCookieString(cookieHeader || '');
    const userAgent = req?.headers['user-agent'] || (isOfficialHome ? appProps.pageProps.userAgent : undefined);
    const theme = query.theme || cookies.theme || 'dark';
    if (req && !injectedCountryCode) {
      // 尚未注入国家码时通过 IP 补齐地区；官网语言由页面数据层独立解析。
      const ip = (req.headers['x-forwarded-for'] || req.socket.remoteAddress || '') as string;
      try {
        // 仅本地开发使用固定 IP，dev 测试环境使用请求 IP 解析真实地区。
        const currentIp = process.env.NEXT_PUBLIC_ENV === 'development' ? '139.9.117.39:80' : ip;
        let prefix = process.env.NEXT_PUBLIC_ENV !== 'release' ? process.env.NEXT_PUBLIC_ENV : '';
        if (prefix === 'development' || prefix === 'dev') {
          prefix = 'pre';
        }
        const urlParams = new URLSearchParams({
          client_id: process.env.NEXT_PUBLIC_REBONEO_CLIENT_ID || '',
          gid: cookies.gid || '',
        });
        const url = `http://${prefix}webapi.roboneo.${(process.env.NEXT_PUBLIC_ENV === 'development' || process.env.NEXT_PUBLIC_ENV === 'dev') ? '' : 'm.'}com/common/ip_resolution.json?${urlParams.toString()}`;
        const geoRes = await fetch(url, {
          method: 'GET', // 也可以省略，fetch 默认是 GET
          headers: {
            'x-forwarded-for': currentIp,
            ...(cookieHeader ? { cookie: cookieHeader } : {}),
          },
        });
        if (!geoRes.ok) {
          const text = await geoRes.text();
          console.error('错误响应体:', text);
          throw new Error(`HTTP 错误，状态码: ${geoRes.status}`);
        } else {
          const geoData = await geoRes.json();
          countryCode = geoData.data.area_code;
          console.error('[IP Resolution]', JSON.stringify({
            countryCode: geoData.data.area_code,
            gid: cookies.gid || '',
            reqid: geoData.reqid,
            currentIp,
            xForwardedFor: req.headers['x-forwarded-for'] || '',
            remoteAddress: req.socket.remoteAddress || '',
            customQuery: query.meituQuery || '',
          }));
        }
      } catch (e) {
        console.warn('Geo IP 语言判断失败', e);
      }
    }
    // 方便测试海外，保持 query 参数拥有最高优先级。
    if (req && (process.env.NEXT_PUBLIC_ENV === 'pre' || process.env.NEXT_PUBLIC_ENV === 'dev' || process.env.NEXT_PUBLIC_ENV === 'development') && typeof query.countryCode === 'string') {
      countryCode = query.countryCode;
    }
    if (isOfficialHome) countryCode = countryCode.trim().toUpperCase() || 'CN';
    return {
      ...appProps,
      pageProps: {
        ...appProps.pageProps,
        countryCode,
        userAgent,
        theme,
      },
    };
  },
});
